import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import ActionButtons from "../../components/action_buttons";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { axiosApi } from "../../../services/axios_api";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { MultiSelect } from "primereact/multiselect";

const EditAddForm = ({
  onHide,
  editable,
  setRes,
  rowData,
  setShowDialog,
  getUsersData,
}) => {
  const [loading, setLoading] = useState(false);
  const [mnemonic, setMnemonicID] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branch, setBranchs] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const response = await axiosApi.get(
          `/Portal/lists/roles`
        );
        setRoles(response.data.body);
      } catch (error) {
        toast.error("Failed to fetch roles data.");
      } finally {
        setLoading(false);
      }
    };
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const response = await axiosApi.get(
          `/Portal/branches/get-all`
        );
        setBranchs(response.data.body);
      } catch (error) {
        toast.error("Failed to fetch roles data.");
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
    fetchBranches();
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string().required(" Name is required."),
    username: Yup.string().required("User Name is required."),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    mobileNumber: Yup.string()
      .required("Mobile Number is required.")
      .matches(
        /^03\d{9}$/,
        "Mobile Number must start with 03 and be 11 digits long."
      )
      .length(11, "Mobile Number must be 11 digits long."),
    roleId: Yup.string().required("Please select a role."),
    isActive: Yup.string().required("Please select Status."),
    branchId: Yup.string().required("Please select Branch."),
    alertPreferences: Yup.string().required(
      "Please select at least one alert preference."
    ),
    password: Yup.string()
      .matches(
        /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}/,
        "Password must be at least 8 characters long and include 1 uppercase letter, 1 lowercase letter, 1 number, and 1 symbol."
      )
      .required("Password is required."),
  });

  const formik = useFormik({
    initialValues: {
      name: rowData?.name || "",
      username: rowData?.username || "",
      mobileNumber: rowData?.mobileNumber || "",
      email: rowData?.email || "",
      password: rowData?.password || "",
      roleId: rowData?.roleId || "",
      isActive: rowData?.isActive || "",
      branchId: rowData?.branchId || "",
      alertPreferences: rowData?.alertPreferences || "",
    },

    validationSchema,
    onSubmit: async (data) => {
      setLoading(true);
      try {
        let response;
        if (editable) {
          data.id = rowData.id;
          response = await axiosApi.put("/Portal/users/edit", data);
        } else {
          response = await axiosApi.post("/Portal/users/add", data);
        }

        console.log("API Response:", response.data);

        if (response.data.responseCode === "200") {
          toast.success(response.data?.responseDescription);
        } else {
          toast.error(
            response.data?.responseDescription || "Something went wrong"
          );
        }
        setRes(response);
        setShowDialog(false);
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="main-form">
      <form onSubmit={formik.handleSubmit}>
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label htmlFor="feeName">
              Name <span className="text-danger"> *</span>
            </label>
            <InputText
              keyfilter={/^[a-zA-Z\s]*$/}
              id="name"
              name="name"
              maxLength={"30"}
              value={formik.values.name}
              placeholder="Enter Name"
              onChange={formik.handleChange}
            />
            {formik.touched.name && formik.errors.name && (
              <small className="p-error">{formik.errors.name}</small>
            )}
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="feeName">
              User Name <span className="text-danger"> *</span>
            </label>
            <InputText
              keyfilter={/^[a-zA-Z0-9\s-]*$/}
              id="username"
              name="username"
              maxLength={"30"}
              value={formik.values.username}
              placeholder="Enter User Name"
              onChange={formik.handleChange}
            />
            {formik.touched.username && formik.errors.username && (
              <small className="p-error">{formik.errors.username}</small>
            )}
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="feeName">
              Email <span className="text-danger"> *</span>
            </label>
            <InputText
              id="email"
              type="email"
              name="email"
              value={formik.values.email}
              placeholder="Enter Email"
              onChange={formik.handleChange}
            />
            {formik.touched.email && formik.errors.email && (
              <small className="p-error">{formik.errors.email}</small>
            )}
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="feeName">
              Mobile Number<span className="text-danger"> *</span>
            </label>
            <InputText
              maxLength={11}
              id="mobileNumber"
              name="mobileNumber"
              value={formik.values.mobileNumber}
              placeholder="Enter Mobile Number"
              onChange={(e) => {
                const sanitizedValue = e.target.value.replace(/[^0-9]/g, "");
                formik.setFieldValue("mobileNumber", sanitizedValue);
              }}
            />
            {formik.touched.mobileNumber && formik.errors.mobileNumber && (
              <small className="p-error">{formik.errors.mobileNumber}</small>
            )}
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="feeName">
              Password<span className="text-danger"> *</span>
            </label>

            <Password
              id="password"
              name="password"
              value={formik.values.password}
              placeholder="Enter Password"
              maxLength={11}
              feedback={false}
              toggleMask
              onChange={(e) => {
                formik.setFieldValue("password", e.target.value);
              }}
            />

            {formik.touched.password && formik.errors.password && (
              <small className="p-error">{formik.errors.password}</small>
            )}
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="alertPreferences">
              Alert Preference <span className="text-danger"> *</span>
            </label>
            <MultiSelect
              id="alertPreferences"
              name="alertPreferences"
              value={
                formik.values.alertPreferences
                  ? formik.values.alertPreferences.split(",")
                  : []
              }
              onChange={(e) => {
                const selectedValues =
                  e.value.length > 0 ? e.value.join(",") : "";
                formik.setFieldValue("alertPreferences", selectedValues);
              }}
              options={[
                { label: "Email", value: "Email" },
                { label: "SMS", value: "SMS" },
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Alert Preferences"
              filter
              display="chip"
            />

            {formik.touched.alertPreferences &&
              formik.errors.alertPreferences && (
                <small className="p-error">
                  {formik.errors.alertPreferences}
                </small>
              )}
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="companyName">
              Role <span className="text-danger"> *</span>
            </label>
            <Dropdown
              filter
              id="roleId"
              name="roleId"
              value={formik.values.roleId}
              onChange={formik.handleChange}
              options={roles.map((roles) => ({
                label: roles.name,
                value: roles.id,
              }))}
              placeholder="Select Role"
            />
            {formik.touched.roleId && formik.errors.roleId && (
              <small className="p-error">{formik.errors.roleId}</small>
            )}
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="companyName">
              Status <span className="text-danger"> *</span>
            </label>
            <Dropdown
              filter
              id="isActive"
              name="isActive"
              value={formik.values.isActive}
              onChange={formik.handleChange}
              options={[
                { label: "Active", value: true },
                { label: "Inactive", value: false },
              ]}
              placeholder="Select Status"
            />
            {formik.touched.isActive && formik.errors.isActive && (
              <small className="p-error">{formik.errors.isActive}</small>
            )}
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="companyName">
              Branch <span className="text-danger"> *</span>
            </label>
            <Dropdown
              filter
              id="branchId"
              name="branchId"
              value={formik.values.branchId}
              onChange={formik.handleChange}
              options={branch.map((branch) => ({
                label: branch.name,
                value: branch.id,
              }))}
              placeholder="Select Branch"
            />
            {formik.touched.branchId && formik.errors.branchId && (
              <small className="p-error">{formik.errors.branchId}</small>
            )}
          </div>
        </div>

        <div className="form-buttons mt-4">
          <ActionButtons
            loading={loading}
            onCancel={onHide}
            onSave={formik.handleSubmit}
            saveLabel={editable ? "Update Changes" : "Save Changes"}
            showSave={true}
            cancelLabel="Cancel"
          />
        </div>
      </form>
    </div>
  );
};

export default EditAddForm;
